import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

const OTPModal = ({ open, onClose, onSubmit, onGenerateOTP, action, isLoading, cooldown }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!open || !action) return;
    setOtp(['', '', '', '', '', '']);
    setError('');
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, [open, action]);

  useEffect(() => {
    if (!open || !action) return;
    if (cooldown > 0) return;
    triggerSendOTP();
  }, [open, action]);

  const triggerSendOTP = async () => {
    if (isSending) return;
    setIsSending(true);
    setError('');
    try {
      await onGenerateOTP(action);
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      requestAnimationFrame(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    if (/^\d{6}$/.test(pastedData)) {
      const otpArray = pastedData.split('');
      setOtp(otpArray);
      setError('');

      setTimeout(() => {
        const lastInput = inputRefs.current[5];
        if (lastInput) {
          lastInput.focus();
        }
      }, 100);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === 'Enter' && otp.join('').length === 6) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }
    setError('');
    onSubmit(otpString);
  };

  const handleClose = () => {
    setOtp(['', '', '', '', '', '']);
    setError('');
    onClose();
  };

  const handleResendOTP = async () => {
    if (cooldown > 0 || isSending) return;
    await triggerSendOTP();
  };

  const formatCooldown = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

  const getTitle = () => {
    const TitleMap = {
      view_secret: 'Enter OTP',
      regenerate_secret: 'Generate a New Client Secret',
    };
    return TitleMap[action] || 'OTP Verification';
  };

  const getDescription = () => {
    const DescriptionMap = {
      view_secret: 'To view your secret, please enter the 6-digit OTP sent to your email address.',
      regenerate_secret: null,
    };
    return DescriptionMap[action] || 'Please enter the OTP sent to your registered email.';
  };

  const getWarningText = () => {
    const WarningMap = {
      regenerate_secret: (
        <>
          <strong>Warning:</strong> Your current secret will be invalidated immediately. Any active integrations will
          stop working until you update them with the new secret.
        </>
      ),
    };

    return WarningMap[action] || null;
  };

  const getButtonText = () =>
    ({
      view_webhook_secret: 'View Secret',
      regenerate_webhook_secret: 'Confirm & Regenerate',
    })[action] || 'Submit';

  const isRegenerateAction = action === 'regenerate_webhook_secret';
  const resendDisabled = isSending || cooldown > 0;
  const submitDisabled = isLoading || otp.join('').length !== 6;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: '435px',
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        {getTitle()}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {getDescription()}
        </Typography>

        {getWarningText() && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              '& .MuiAlert-icon': {
                alignItems: 'flex-start',
                mt: 0.5,
              },
            }}
            icon={
              <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                ⚠️
              </Typography>
            }
          >
            {getWarningText()}
          </Alert>
        )}

        {['regenerate_secret'].includes(action) && (
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
            Enter the 6-digit OTP sent to your email to confirm:
          </Typography>
        )}

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                variant="outlined"
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    width: '32px',
                    height: '32px',
                  },
                }}
                error={!!error}
                sx={{
                  width: '56px',
                  margin: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: index === 0 ? '4px 0 0 4px' : index === 5 ? '0 4px 4px 0' : 0,
                    '& fieldset': {
                      borderColor: error ? 'error.main' : 'grey.300',
                      borderRightWidth: index === 5 ? 1 : 0,
                    },
                    '&:hover fieldset': {
                      borderColor: error ? 'error.main' : 'grey.400',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: error ? 'error.main' : 'primary.main',
                    },
                  },
                }}
              />
            ))}
          </Box>
          {error && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', alignItems: 'center' }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isRegenerateAction ? 'error' : 'primary'}
          disabled={submitDisabled}
          sx={{
            width: '100%',
            mb: 2,
            fontWeight: 'bold',
            py: 1.5,
            textTransform: 'none',
            ...(isRegenerateAction && {
              backgroundColor: 'error.main',
              '&:hover': { backgroundColor: 'error.dark' },
              '&:disabled': { backgroundColor: 'rgba(211, 47, 47, 0.12)', color: 'white' },
            }),
          }}
        >
          {isLoading ? (isRegenerateAction ? 'Processing...' : 'Verifying...') : getButtonText()}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Didn&#39;t receive the OTP?{' '}
          <Button
            variant="text"
            onClick={handleResendOTP}
            disabled={resendDisabled}
            sx={{ p: 0, minWidth: 'auto', fontWeight: 'bold', color: 'primary.main', textTransform: 'none' }}
          >
            {cooldown > 0 ? `Resend OTP in ${formatCooldown(cooldown)}` : 'Resend OTP'}
          </Button>
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default OTPModal;
